/**
 * The UV operators the engine runs (`modelOp`, runtime/uv_tool.rs) with the
 * options their dialogs ask for, Blender's defaults.
 */

export type UvParamValue = number | boolean | string;

export interface UvOpField {
  key: string;
  label: string;
  kind: 'number' | 'int' | 'bool' | 'select';
  default: UvParamValue;
  min?: number;
  max?: number;
  step?: number;
  options?: readonly string[];
  hint?: string;
}

export interface UvOpDef {
  id: string;
  label: string;
  /** `modelOp` name. */
  op: string;
  /** Fixed parameters. */
  params?: Record<string, UvParamValue>;
  /** Options asked in a dialog before running (remembered per session). */
  fields?: readonly UvOpField[];
  /** Needs selected UVs (Stitch). */
  needsSelection?: boolean;
  keywords?: string;
}

const margin: UvOpField = { key: 'margin', label: 'Island margin', kind: 'number', default: 0.01, min: 0, max: 1, step: 0.001, hint: 'space between islands, in UV units' };

export const UV_OPS = {
  unwrapAngle: { id: 'unwrapAngle', label: 'Unwrap (Angle Based)', op: 'uvUnwrap', params: { method: 'angle' }, keywords: 'abf lscm seams' },
  unwrapConformal: { id: 'unwrapConformal', label: 'Unwrap (Conformal)', op: 'uvUnwrap', params: { method: 'conformal' }, keywords: 'lscm seams' },
  smartProject: {
    id: 'smartProject',
    label: 'Smart UV Project',
    op: 'uvSmartProject',
    fields: [
      { key: 'angleLimit', label: 'Angle limit (°)', kind: 'number', default: 66, min: 1, max: 89, step: 1, hint: 'faces bending more than this start a new island' },
      margin,
      { key: 'areaWeight', label: 'Area weight', kind: 'number', default: 0, min: 0, max: 1, step: 0.05, hint: 'favour large faces when choosing projection directions' },
    ],
    keywords: 'automatic project',
  },
  cube: {
    id: 'cube',
    label: 'Cube Projection',
    op: 'uvProject',
    params: { kind: 'cube' },
    fields: [{ key: 'size', label: 'Cube size', kind: 'number', default: 1, min: 0.001, step: 0.1, hint: 'meters per UV tile' }],
    keywords: 'box',
  },
  cylinder: {
    id: 'cylinder',
    label: 'Cylinder Projection',
    op: 'uvProject',
    params: { kind: 'cylinder' },
    fields: [{ key: 'axis', label: 'Axis', kind: 'select', default: 'y', options: ['x', 'y', 'z'] }],
  },
  sphere: {
    id: 'sphere',
    label: 'Sphere Projection',
    op: 'uvProject',
    params: { kind: 'sphere' },
    fields: [{ key: 'axis', label: 'Axis', kind: 'select', default: 'y', options: ['x', 'y', 'z'] }],
  },
  projectView: { id: 'projectView', label: 'Project from View', op: 'uvProject', params: { kind: 'view' }, keywords: 'camera' },
  pack: {
    id: 'pack',
    label: 'Pack Islands',
    op: 'uvPack',
    fields: [margin, { key: 'rotate', label: 'Rotate', kind: 'bool', default: true, hint: 'turn islands for a tighter fit' }],
    keywords: 'layout atlas',
  },
  averageScale: { id: 'averageScale', label: 'Average Islands Scale', op: 'uvAverageScale', keywords: 'texel uniform' },
  minimizeStretch: {
    id: 'minimizeStretch',
    label: 'Minimize Stretch',
    op: 'uvMinimizeStretch',
    fields: [{ key: 'iterations', label: 'Iterations', kind: 'int', default: 10, min: 1, max: 500, step: 1 }],
    keywords: 'relax',
  },
  stitch: { id: 'stitch', label: 'Stitch', op: 'uvStitch', needsSelection: true, keywords: 'weld merge seam' },
} satisfies Record<string, UvOpDef>;

export type UvOpId = keyof typeof UV_OPS;

export function uvOp(id: UvOpId): UvOpDef {
  return UV_OPS[id];
}

/** Menu / command label ("…" when a dialog asks for options first). */
export function uvOpLabel(id: UvOpId): string {
  const def = uvOp(id);
  return def.fields ? `${def.label}…` : def.label;
}

/** Field defaults overlaid with remembered values. */
export function opParams(def: UvOpDef, remembered: Record<string, UvParamValue> | undefined): Record<string, UvParamValue> {
  const out: Record<string, UvParamValue> = {};
  for (const f of def.fields ?? []) out[f.key] = remembered?.[f.key] ?? f.default;
  return out;
}

/** The `modelOp` parameters for a run on a selection. */
export function opCommand(
  def: UvOpDef,
  options: Record<string, UvParamValue>,
  selection: { faces: readonly number[]; uvs: readonly number[] },
): Record<string, unknown> {
  return {
    ...(def.params ?? {}),
    ...options,
    ...(selection.faces.length ? { faces: selection.faces } : {}),
    ...(selection.uvs.length ? { uvs: selection.uvs } : {}),
  };
}
